// Um balão da thread.
//
// `direction` é do ponto de vista da EMPRESA, não do usuário logado: outbound é
// tudo que saiu do nosso número, inclusive o eco de uma mensagem digitada no
// celular por outra pessoa (que chega sem `sender_user_id`). Por isso o balão
// da direita nem sempre tem autor — e não pode assumir que fui eu.
//
// WhatsApp completo (23/10/2026): citação no topo do balão ("responder"),
// reações penduradas embaixo, figurinha sem fundo e, ao passar o mouse, os
// botões Responder e Reagir. Os botões só aparecem quando quem usa passa
// `onResponder`/`onReagir` (hoje: conversas de WhatsApp) e quando a mensagem
// já saiu do WhatsApp — sem id do provider não dá para citar nem reagir.

import React, { useState } from 'react';
import { Check, CheckCheck, Clock, CornerUpLeft, RotateCcw, SmilePlus, TriangleAlert } from 'lucide-react';
import type { Mensagem } from '../tipos';
import { horaCurta } from '../formato';
import AnexoMensagem from './AnexoMensagem';
import CorpoEmail from './CorpoEmail';

/** As reações rápidas, na ordem do app. */
export const REACOES_RAPIDAS = ['👍', '❤️', '😂', '😮', '😢', '🙏'];

interface Props {
  mensagem: Mensagem;
  /** Nome de quem respondeu, quando dá para saber. */
  autor?: string | null;
  /** Encosta no balão anterior: mesma direção, poucos minutos de diferença. */
  agrupado: boolean;
  /** Sem ele o balão é só leitura (aba WhatsApp da negociação): falha aparece, botão de reenviar não. */
  onReenviar?: (id: string) => void;
  reenviando?: boolean;
  /** Mensagem citada, já resolvida por quem desenha a conversa. */
  citada?: { autor: string; texto: string } | null;
  onResponder?: (m: Mensagem) => void;
  onReagir?: (m: Mensagem, emoji: string) => void;
}

function Selo({ m }: { m: Mensagem }) {
  if (m.direction !== 'outbound') return null;
  if (m.status === 'failed') return <TriangleAlert size={13} className="text-red-200" />;
  if (m.status === 'queued') return <Clock size={13} className="text-white/60" />;
  if (m.status === 'read') return <CheckCheck size={13} className="text-sky-200" />;
  if (m.status === 'delivered') return <CheckCheck size={13} className="text-white/70" />;
  return <Check size={13} className="text-white/70" />;
}

function BalaoBase({ mensagem: m, autor, agrupado, onReenviar, reenviando, citada, onResponder, onReagir }: Props) {
  const [paletaAberta, setPaletaAberta] = useState(false);
  const meu = m.direction === 'outbound';
  const falhou = m.status === 'failed';
  // E-mail: o que se mostra e o corpo SEM a citacao; o `body` guarda a mensagem
  // inteira. Mostrar o `body` faria cada resposta repetir a conversa toda.
  const textoVisivel = m.body_visible ?? m.body;
  const ehEmail = m.type === 'email' || !!m.body_html || !!m.subject;
  const temCorpo = !!(textoVisivel && textoVisivel.trim()) || !!m.body_html || !!m.subject;
  const temAnexo = !!m.anexos?.length;
  const ehFigurinha = m.type === 'sticker' && temAnexo;
  const reacoes = [m.payload?.reacoes?.cliente?.emoji, m.payload?.reacoes?.nos?.emoji].filter(Boolean) as string[];
  const podeAgir = !!m.provider_message_id && !m.local;

  // Mensagem de sistema não é conversa: é registro. Fica centralizada e discreta.
  if (m.type === 'system') {
    // 78 mensagens de sistema do WhatsApp chegam sem texto (23/10/2026) e
    // virariam uma pílula cinza vazia no meio da conversa.
    if (!m.body?.trim()) return null;
    return (
      <div className="flex justify-center py-1">
        <span className="rounded-full bg-gray-100 px-3 py-1 text-[11px] text-gray-500 dark:bg-slate-700/60 dark:text-slate-400">
          {m.body}
        </span>
      </div>
    );
  }

  const acoes = (onResponder || onReagir) && podeAgir && (
    <div
      className={`absolute top-1 z-10 hidden items-center gap-0.5 rounded-full bg-white px-1 py-0.5 shadow ring-1 ring-gray-200 group-hover:flex dark:bg-slate-800 dark:ring-slate-600 ${
        meu ? 'right-full mr-1.5' : 'left-full ml-1.5'
      } ${paletaAberta ? '!flex' : ''}`}
    >
      {onReagir && (paletaAberta ? (
        REACOES_RAPIDAS.map((e) => (
          <button
            key={e}
            onClick={() => { onReagir(m, e); setPaletaAberta(false); }}
            className={`rounded-full px-1 text-base leading-6 transition-transform hover:scale-125 ${m.payload?.reacoes?.nos?.emoji === e ? 'bg-primary/15' : ''}`}
            title={m.payload?.reacoes?.nos?.emoji === e ? 'Tirar a reação' : 'Reagir'}
          >
            {e}
          </button>
        ))
      ) : (
        <button onClick={() => setPaletaAberta(true)} title="Reagir" className="rounded-full p-1 text-gray-500 hover:bg-gray-100 hover:text-primary dark:text-slate-300 dark:hover:bg-slate-700">
          <SmilePlus size={15} />
        </button>
      ))}
      {onResponder && !paletaAberta && (
        <button onClick={() => onResponder(m)} title="Responder" className="rounded-full p-1 text-gray-500 hover:bg-gray-100 hover:text-primary dark:text-slate-300 dark:hover:bg-slate-700">
          <CornerUpLeft size={15} />
        </button>
      )}
    </div>
  );

  const blocoCitacao = citada && (
    <div className={`mb-1.5 rounded-lg border-l-4 px-2 py-1 text-xs ${
      meu ? 'border-white/70 bg-white/15 text-white/90' : 'border-primary/60 bg-gray-100 text-gray-600 dark:bg-slate-600/60 dark:text-slate-200'
    }`}>
      <p className={`font-semibold ${meu ? 'text-white' : 'text-primary dark:text-[#B4C77A]'}`}>{citada.autor}</p>
      <p className="line-clamp-2 whitespace-pre-wrap break-words">{citada.texto}</p>
    </div>
  );

  return (
    <div
      className={`flex ${meu ? 'justify-end' : 'justify-start'} ${agrupado ? 'mt-0.5' : 'mt-2.5'} ${reacoes.length ? 'mb-3' : ''}`}
      onMouseLeave={() => setPaletaAberta(false)}
    >
      <div className={`group relative flex flex-col items-stretch ${ehEmail ? 'max-w-[92%] w-full' : 'max-w-[76%]'}`}>
        {acoes}

        {ehFigurinha ? (
          // Figurinha não tem balão, como no app: só a imagem e a hora.
          <div className={`flex flex-col ${meu ? 'items-end' : 'items-start'} ${m.local ? 'opacity-70' : ''}`}>
            {blocoCitacao && <div className="max-w-[220px]">{blocoCitacao}</div>}
            <AnexoMensagem anexo={m.anexos![0]} meu={meu} figurinha />
            <span className="mt-0.5 flex items-center gap-1 rounded-full bg-black/40 px-1.5 text-[10px] text-white">
              {horaCurta(m.provider_timestamp)}
              <Selo m={m} />
            </span>
          </div>
        ) : (
          <div
            className={`overflow-hidden rounded-2xl px-3 py-2 shadow-sm ring-1 ${
              meu
                ? `bg-primary text-white ring-black/5 ${agrupado ? '' : 'rounded-br-md'} ${m.local ? 'opacity-70' : ''}`
                : `bg-white text-gray-800 ring-gray-200/70 dark:bg-slate-700 dark:text-slate-100 dark:ring-slate-600/50 ${agrupado ? '' : 'rounded-bl-md'}`
            } ${falhou ? 'ring-2 ring-red-400' : ''}`}
          >
            {autor && !agrupado && meu && (
              <p className="mb-0.5 text-[11px] font-semibold text-white/70">{autor}</p>
            )}

            {blocoCitacao}

            {temAnexo && (
              <div className={`flex flex-col gap-1.5 ${temCorpo ? 'mb-1.5' : ''}`}>
                {m.anexos!.map((a) => (
                  <AnexoMensagem key={a.id} anexo={a} meu={meu} />
                ))}
              </div>
            )}

            {temCorpo && ehEmail && (
              // O corpo do e-mail vai para um iframe sandbox: e HTML escrito por
              // um estranho, e o atendente vai abrir. Ver `emailSeguro.ts`.
              <CorpoEmail
                assunto={m.subject}
                html={m.body_html}
                texto={textoVisivel}
                destinatarios={m.recipients}
              />
            )}

            {temCorpo && !ehEmail && (
              <p className="whitespace-pre-wrap break-words text-sm leading-relaxed">{textoVisivel}</p>
            )}

            {!temCorpo && !temAnexo && (
              <p className={`text-sm italic ${meu ? 'text-white/70' : 'text-gray-400 dark:text-slate-500'}`}>
                mensagem sem conteúdo de texto
              </p>
            )}

            <div className={`mt-0.5 flex items-center justify-end gap-1 ${meu ? 'text-white/70' : 'text-gray-400 dark:text-slate-500'}`}>
              <span className="text-[10px] tabular-nums">{horaCurta(m.provider_timestamp)}</span>
              <Selo m={m} />
            </div>
          </div>
        )}

        {reacoes.length > 0 && (
          // Pendurada na borda de baixo, como no app.
          <div className={`absolute -bottom-3 ${meu ? 'right-2' : 'left-2'}`}>
            <span className="inline-flex items-center gap-0.5 rounded-full bg-white px-1.5 py-0.5 text-sm leading-none shadow ring-1 ring-gray-200 dark:bg-slate-800 dark:ring-slate-600">
              {reacoes.map((e, i) => <span key={i}>{e}</span>)}
            </span>
          </div>
        )}

        {falhou && (
          <div className={`flex items-center justify-end gap-2 ${reacoes.length ? 'mt-4' : 'mt-1'}`}>
            <span className="text-[11px] text-red-600 dark:text-red-400">
              {m.failure_reason || 'Não foi enviada.'}
            </span>
            {onReenviar && (
            <button
              onClick={() => onReenviar(m.id)}
              disabled={reenviando}
              className="inline-flex items-center gap-1 rounded-lg border border-red-200 px-2 py-0.5 text-[11px] font-semibold text-red-600 transition-colors hover:bg-red-50 disabled:opacity-50 dark:border-red-900/60 dark:text-red-400 dark:hover:bg-red-900/20"
            >
              <RotateCcw size={11} className={reenviando ? 'animate-spin' : ''} />
              {reenviando ? 'Reenviando…' : 'Tentar de novo'}
            </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Memo com comparação explícita.
 *
 * Numa conversa longa (a de teste tem 520 mensagens) o Thread re-renderiza por
 * motivos que nada têm a ver com os balões: o timer que relê as conexões, a
 * recarga da lista da esquerda, uma tecla no composer. Sem memo, cada um desses
 * recria 520 balões — e é aí que a rolagem começa a engasgar.
 *
 * O que muda um balão já pintado: status de entrega, motivo da falha, corpo,
 * anexos, reações, citação, agrupamento e o estado do botão de reenvio. O `id`
 * na comparação garante que reciclagem de posição não mostre a mensagem errada.
 */
export default React.memo(BalaoBase, (a, b) =>
  a.mensagem.id === b.mensagem.id &&
  a.mensagem.status === b.mensagem.status &&
  a.mensagem.failure_reason === b.mensagem.failure_reason &&
  a.mensagem.body === b.mensagem.body &&
  a.mensagem.body_html === b.mensagem.body_html &&
  a.mensagem.subject === b.mensagem.subject &&
  a.mensagem.provider_message_id === b.mensagem.provider_message_id &&
  a.mensagem.anexos?.length === b.mensagem.anexos?.length &&
  a.mensagem.payload?.reacoes?.cliente?.emoji === b.mensagem.payload?.reacoes?.cliente?.emoji &&
  a.mensagem.payload?.reacoes?.nos?.emoji === b.mensagem.payload?.reacoes?.nos?.emoji &&
  a.citada?.texto === b.citada?.texto &&
  a.agrupado === b.agrupado &&
  a.autor === b.autor &&
  a.reenviando === b.reenviando &&
  !!a.onResponder === !!b.onResponder &&
  !!a.onReagir === !!b.onReagir,
);
